import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  EvervaultProvider as EvervaultProviderComponent,
  useEvervault
} from '@evervault/react'
import { CardProvider } from 'lib/forms/types'
import { actionTracking } from 'utils/posthog'

import {
  collectEvervaultResourceTiming,
  createEvervaultErrorBreadcrumbs,
  serializeErrorArg,
  type EvervaultBreadcrumbRecorder
} from './evervaultDiagnostics'
import {
  EvervaultLoadState,
  EvervaultLoadStateContext,
  EvervaultLoadStatus
} from './evervaultLoadState'

export interface EvervaultSdkProviderProps {
  teamId: string
  appId: string
  children: React.ReactNode
}

interface SdkProviderHandle {
  reload: () => void
}

interface LoadWatcherProps {
  attempt: number
  reload: () => void
  children: React.ReactNode
}

// `@evervault/react` injects js.evervault.com once at provider mount and
// caches the rejection for the page lifetime, so a single failed load used
// to leave every card form dead until a hard refresh. The SDK never calls
// its `onLoadError` prop (2.20.0), so the only failure signal is the client
// promise from `useEvervault()` rejecting. Watch it here, expose the status
// and the provider's `reload()` to the forms, and record the failure with
// diagnostics captured before the script tag was ever appended.
const EvervaultLoadWatcher = ({
  attempt,
  reload,
  children
}: LoadWatcherProps) => {
  const client = useEvervault()
  const [status, setStatus] = useState<EvervaultLoadStatus>('loading')
  const [error, setError] = useState<Error | null>(null)
  const breadcrumbsRef = useRef<EvervaultBreadcrumbRecorder | null>(null)

  // Child effects run before the SDK provider's own effect appends the
  // script, so this recorder sees the load failure itself.
  useEffect(() => {
    const recorder = createEvervaultErrorBreadcrumbs()
    breadcrumbsRef.current = recorder
    return () => {
      recorder.dispose()
      breadcrumbsRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!client) return
    let active = true
    setStatus('loading')
    setError(null)

    client.then(
      () => {
        if (!active) return
        setStatus('ready')
        if (attempt > 0) {
          actionTracking({
            action: 'card_sdk_load_recovered',
            properties: { provider: CardProvider.Evervault, attempt }
          })
        }
      },
      (err: unknown) => {
        if (!active) return
        setStatus('error')
        setError(err instanceof Error ? err : new Error(String(err)))
        actionTracking({
          action: 'card_sdk_load_failed',
          properties: {
            provider: CardProvider.Evervault,
            attempt,
            errorArg: serializeErrorArg(err),
            online:
              typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            evervaultResources: collectEvervaultResourceTiming(),
            breadcrumb: breadcrumbsRef.current?.getLatest() ?? null
          }
        })
      }
    )

    return () => {
      active = false
    }
  }, [client, attempt])

  const value = useMemo<EvervaultLoadState>(
    () => ({ status, error, attempt, reload }),
    [status, error, attempt, reload]
  )

  return (
    <EvervaultLoadStateContext.Provider value={value}>
      {children}
    </EvervaultLoadStateContext.Provider>
  )
}

export const EvervaultSdkProvider = ({
  teamId,
  appId,
  children
}: EvervaultSdkProviderProps) => {
  const sdkRef = useRef<SdkProviderHandle>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => {
    sdkRef.current?.reload()
    setAttempt(current => current + 1)
  }, [])

  return (
    <EvervaultProviderComponent ref={sdkRef} teamId={teamId} appId={appId}>
      <EvervaultLoadWatcher attempt={attempt} reload={reload}>
        {children}
      </EvervaultLoadWatcher>
    </EvervaultProviderComponent>
  )
}

export default EvervaultSdkProvider
